import { useMemo, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import Typography from '@mui/material/Typography';
import {
  Button,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  useTheme,
  ButtonProps,
  FormControl
} from '@mui/material';
import { Box } from '@mui/system';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import InputPostLink from '../InputPostLink';
import { useAppDispatch, useAppSelector } from 'src/app/hooks';
import NotAllowedTooltip from '../NotAllowedTooltip';
import DataCollectionInformation from '../DataCollectionInformation';
import InputCardSocialProfile from '../Retail/ModalManageObjectRetail/components/InputCardSocialProfile';
import { IObject, IObjectInput } from 'src/models/objectManagement';
import useOnboarding from 'src/services/useOnboarding';
import useObjectManagement from 'src/services/useObjectManagement';
import useUsers from 'src/services/useUsers';
import { useApolloClient } from '@apollo/client';
import {
  GET_OBJECTS_WITH_ENTITY,
  GET_OBJECTS_WITH_STATUS
} from 'src/services/graphql/objectManagement/query';
import Confirmation from '../Confirmation';
import {
  reducerRemoveTempObjectInput,
  reducerUpdateAllTempObjectInput,
  reducerUpdateTempObjectInput
} from 'src/features/objectManagement';

interface Props {
  title: string;
  showButton: boolean;
  buttonProps?: ButtonProps;
  buttonLabel?: string;
  handleCloseModal: () => void;
  onDone?: () => void;
}

const ModalAddPostLink: React.FC<Props> = ({
  title,
  showButton,
  buttonProps = {},
  buttonLabel,
  handleCloseModal,
  onDone
}) => {
  const [open, setOpen] = useState(showButton ? false : true);
  const { role: userRole, id: projectId } = useAppSelector(
    (state) => state.users.project
  );

  const dispatch = useAppDispatch();

  const { tempInputObjects, objectListWithStatus } = useAppSelector(
    (state) => state.objectManagement
  );

  const { createObjects, responseCreateObjects } = useOnboarding();
  const { getObjectList } = useObjectManagement();
  const { getProjectDetailResult } = useUsers();
  const client = useApolloClient();
  const [openAddConfirmation, setOpenAddConfirmation] =
    useState<boolean>(false);

  const objectListPostLink = useMemo((): Array<IObject> => {
    return objectListWithStatus.filter(
      (object) => object.streamType === 'post_link'
    );
  }, [objectListWithStatus]);

  const handleClickOpen = () => {
    dispatch(reducerUpdateAllTempObjectInput([]));
    setOpen(true);
  };

  const handleClose = () => {
    dispatch(reducerUpdateAllTempObjectInput([]));
    setOpen(false);
    handleCloseModal();
  };

  const handleSubmit = () => {
    const socialProfilePayload = {
      projectId: projectId,
      streamType: 'post_link',
      objectInput: [
        ...tempInputObjects.map(
          ({ streamType, ...socialProfile }) => socialProfile
        )
      ]
    };
    createObjects({
      variables: socialProfilePayload
    }).then(() => {
      getObjectList(projectId);
      handleClose();
      getProjectDetailResult(projectId);
      client.refetchQueries({
        include: [GET_OBJECTS_WITH_ENTITY, GET_OBJECTS_WITH_STATUS]
      });
      if (onDone) {
        onDone();
      }
    });
  };

  const handleAddObject = (inputObject: IObjectInput) => {
    dispatch(reducerUpdateTempObjectInput(inputObject));
  };

  const handleRemove = (id: string) => {
    dispatch(reducerRemoveTempObjectInput(id));
  };

  const isObjectAlreadyIncluded = () => {
    return objectListPostLink.some((object) =>
      tempInputObjects
        .map((item) => item.objectName)
        .includes(object.objectName)
    );
  };

  const theme = useTheme();

  const isMember = userRole === 'member';

  return (
    <>
      {showButton ? (
        isMember ? (
          <NotAllowedTooltip>
            <Button
              variant="outlined"
              endIcon={<AddIcon />}
              {...buttonProps}
              sx={{ ...buttonProps.sx }}
            >
              {buttonLabel ? buttonLabel : 'Social Profile'}
            </Button>
          </NotAllowedTooltip>
        ) : (
          <Button
            variant="outlined"
            onClick={handleClickOpen}
            endIcon={<AddIcon />}
            {...buttonProps}
            sx={{ ...buttonProps.sx }}
          >
            {buttonLabel ? buttonLabel : 'Social Profile'}
          </Button>
        )
      ) : null}
      <Dialog onClose={handleClose} open={open} fullWidth maxWidth={'md'}>
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500]
          }}
        >
          <CloseIcon />
        </IconButton>
        <DialogTitle
          sx={{
            m: theme.spacing(3, 3, 0),
            p: theme.spacing(2, 3, 0, 3)
          }}
        >
          <Typography variant="h4">{title} </Typography>
        </DialogTitle>
        <DialogContent
          sx={{
            m: { xs: 0, sm: theme.spacing(1, 3) }
          }}
        >
          <DataCollectionInformation />
          <Grid container spacing={2} sx={{ my: 1 }}>
            <Grid item lg={6}>
              <InputPostLink onAdd={handleAddObject} />
            </Grid>
            <Grid item lg={6}>
              <FormControl fullWidth size="medium">
                <InputCardSocialProfile
                  title={`Added Post Link`}
                  information="Tracked post link will appear here"
                  objectList={tempInputObjects}
                  onRemove={handleRemove}
                />
              </FormControl>
            </Grid>
            <Grid item lg={12}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  gap: 1
                }}
              >
                <Button
                  variant="contained"
                  color="primary"
                  sx={{ width: theme.spacing(15), height: theme.spacing(5) }}
                  disabled={
                    responseCreateObjects.loading ||
                    tempInputObjects.length === 0
                  }
                  onClick={() =>
                    isObjectAlreadyIncluded()
                      ? setOpenAddConfirmation(true)
                      : handleSubmit()
                  }
                >
                  Done
                </Button>
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
      </Dialog>
      <Confirmation
        title={'Add this object?'}
        labelButton={'Add'}
        message={`The post link you want to add has already been added.`}
        open={openAddConfirmation}
        onClose={() => setOpenAddConfirmation(false)}
        onOk={() => {
          handleSubmit();
        }}
      />
    </>
  );
};

export default ModalAddPostLink;
